import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth0 } from "@auth0/auth0-react";

// Hooks
import { useRefinanceAccounts } from '../hooks/useRefinanceAccounts';
import { useResponseMessages } from "../hooks/useResponseMessage";

// Components
import RefinanceAccountItem from '../components/Refinance/RefinanceAccountItem';
import NewLoanForm from '../components/Refinance/NewLoanForm';
import RefinanceComparison from '../components/Refinance/RefinanceComparison';
import ManualAccountForm from '../components/Refinance/ManualAccountForm';

// Utilities
import { formatCurrency } from '../utilities/general-utils';
import { calculateLoanPayoff, calculateLoanPayoffWithDynamicMinimum } from '../utilities/refinance-utilities';
import { getDefaultPaymentAmount } from '../utilities/account-utilities';

// Styles
import '../assets/css/page/RefinanceCalculator.css';

const RefinanceCalculator = () => {
    const { getAccessTokenSilently, user } = useAuth0();
    const { addMessage } = useResponseMessages();

    // Account management hook
    const {
        debtAccounts,
        isLoading: isLoadingAccounts,
        getAccounts,
        handleRefetchAccounts
    } = useRefinanceAccounts();

    // Local state
    const [selectedAccountIds, setSelectedAccountIds] = useState(new Set());
    const [manualAccounts, setManualAccounts] = useState([]);
    const [showManualForm, setShowManualForm] = useState(false);
    const [sortBy, setSortBy] = useState('balance'); // 'balance', 'apr', 'monthlyPayment'
    const [newLoanData, setNewLoanData] = useState({
        balance: '',
        apr: '',
        monthlyPayment: '',
        termMonths: '',
        loanType: 'Personal Loan'
    });

    // All accounts (debt + manual)
    const allAccounts = useMemo(() => {
        return [...debtAccounts, ...manualAccounts];
    }, [debtAccounts, manualAccounts]);

    // Sorted accounts based on selected criteria
    const sortedAccounts = useMemo(() => {
        const accounts = [...allAccounts];
        
        accounts.sort((a, b) => {
            let aValue, bValue;
            
            switch (sortBy) {
                case 'balance':
                    aValue = parseFloat(a.balance) || 0;
                    bValue = parseFloat(b.balance) || 0;
                    return bValue - aValue; // High to low
                    
                case 'apr':
                    aValue = parseFloat(a.apr) || 0;
                    bValue = parseFloat(b.apr) || 0;
                    return bValue - aValue; // High to low
                    
                case 'monthlyPayment':
                    aValue = parseFloat(a.monthly_payment) || parseFloat(a.defaultPaymentAmount) || parseFloat(a.minimum_payment) || 0;
                    bValue = parseFloat(b.monthly_payment) || parseFloat(b.defaultPaymentAmount) || parseFloat(b.minimum_payment) || 0;
                    return bValue - aValue; // High to low
                    
                default:
                    return 0;
            }
        });
        
        return accounts;
    }, [allAccounts, sortBy]);

    // Selected accounts data
    const selectedAccounts = useMemo(() => {
        return sortedAccounts.filter(account => selectedAccountIds.has(account.id));
    }, [sortedAccounts, selectedAccountIds]);

    // Totals for selected accounts
    const selectedTotals = useMemo(() => {
        return selectedAccounts.reduce((totals, account) => ({
            balance: totals.balance + (parseFloat(account.balance) || 0),
            monthlyPayment: totals.monthlyPayment + (parseFloat(account.monthly_payment) || parseFloat(account.defaultPaymentAmount) || parseFloat(account.minimum_payment) || 0)
        }), { balance: 0, monthlyPayment: 0 });
    }, [selectedAccounts]);

    // Calculate comparison data
    const comparisonData = useMemo(() => {
        if (selectedAccounts.length === 0 || !newLoanData.balance || !newLoanData.apr) {
            return null;
        }

        // Calculate current scenario (sum of all selected accounts)
        const currentTotalInterest = selectedAccounts.reduce((sum, account) => {
            const monthlyPayment = parseFloat(account.monthly_payment) || parseFloat(account.defaultPaymentAmount) || parseFloat(account.minimum_payment) || 0;
            
            let payoffData;
            // Use dynamic minimum payment calculation for accounts using minimum payments
            if (account.paymentType === 'minimum' && account.minimum_payment_percentage) {
                payoffData = calculateLoanPayoffWithDynamicMinimum(
                    parseFloat(account.balance) || 0,
                    parseFloat(account.apr) || 0,
                    parseFloat(account.minimum_payment_percentage) || 0.02,
                    parseFloat(account.minimum_payment_low_balance) || 25
                );
            } else {
                // Use fixed payment calculation for accounts with fixed payments
                payoffData = calculateLoanPayoff(
                    parseFloat(account.balance) || 0,
                    parseFloat(account.apr) || 0,
                    monthlyPayment
                );
            }
            
            return sum + payoffData.totalInterest;
        }, 0);

        const currentMaxMonths = Math.max(...selectedAccounts.map(account => {
            const monthlyPayment = parseFloat(account.monthly_payment) || parseFloat(account.defaultPaymentAmount) || parseFloat(account.minimum_payment) || 0;
            
            let payoffData;
            // Use dynamic minimum payment calculation for accounts using minimum payments
            if (account.paymentType === 'minimum' && account.minimum_payment_percentage) {
                payoffData = calculateLoanPayoffWithDynamicMinimum(
                    parseFloat(account.balance) || 0,
                    parseFloat(account.apr) || 0,
                    parseFloat(account.minimum_payment_percentage) || 0.02,
                    parseFloat(account.minimum_payment_low_balance) || 25
                );
            } else {
                // Use fixed payment calculation for accounts with fixed payments
                payoffData = calculateLoanPayoff(
                    parseFloat(account.balance) || 0,
                    parseFloat(account.apr) || 0,
                    monthlyPayment
                );
            }
            
            return payoffData.monthsToPayoff;
        }));

        // Calculate new loan scenario
        const newLoanBalance = parseFloat(newLoanData.balance) || selectedTotals.balance;
        const newLoanPayoffData = calculateLoanPayoff(
            newLoanBalance,
            parseFloat(newLoanData.apr) / 100 || 0, // Convert percentage input to decimal
            parseFloat(newLoanData.monthlyPayment) || 0
        );

        return {
            current: {
                totalBalance: selectedTotals.balance,
                monthlyPayment: selectedTotals.monthlyPayment,
                totalInterest: currentTotalInterest,
                monthsToPayoff: currentMaxMonths
            },
            newLoan: {
                totalBalance: newLoanBalance,
                monthlyPayment: parseFloat(newLoanData.monthlyPayment) || 0,
                totalInterest: newLoanPayoffData.totalInterest,
                monthsToPayoff: newLoanPayoffData.monthsToPayoff
            }
        };
    }, [selectedAccounts, newLoanData, selectedTotals]);

    // Handle account selection
    const handleAccountSelect = useCallback((accountId, isSelected) => {
        setSelectedAccountIds(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(accountId);
            } else {
                newSet.delete(accountId);
                // If user manually deselects any account, mark as manually deselected
                setHasManuallyDeselected(true);
            }
            return newSet;
        });
    }, []);

    // Handle toggle all accounts
    const handleToggleAllAccounts = useCallback(() => {
        const allAccountIds = sortedAccounts.map(account => account.id);
        const isAllSelected = allAccountIds.every(id => selectedAccountIds.has(id));
        
        if (isAllSelected) {
            // If all are selected, deselect all
            setSelectedAccountIds(new Set());
            setHasManuallyDeselected(true);
        } else {
            // If not all are selected, select all
            setSelectedAccountIds(new Set(allAccountIds));
            setHasManuallyDeselected(false);
        }
    }, [sortedAccounts, selectedAccountIds]);

    // Handle manual account creation
    const handleManualAccountCreate = useCallback((accountData) => {
        const newAccount = {
            id: `manual_${Date.now()}`,
            ...accountData,
            isManual: true
        };
        
        setManualAccounts(prev => [...prev, newAccount]);
        setShowManualForm(false);
        addMessage({
            data: 'Manual account added successfully!'
        });
    }, [addMessage]);

    // Handle manual account deletion
    const handleManualAccountDelete = useCallback((accountId) => {
        setManualAccounts(prev => prev.filter(account => account.id !== accountId));
        setSelectedAccountIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(accountId);
            return newSet;
        });
        addMessage({
            data: 'Manual account removed successfully!'
        });
    }, [addMessage]);

    // Auto-select all accounts initially when loaded
    const [hasManuallyDeselected, setHasManuallyDeselected] = useState(false);
    
    useEffect(() => {
        if (allAccounts.length > 0 && selectedAccountIds.size === 0 && !hasManuallyDeselected) {
            const allIds = new Set(allAccounts.map(account => account.id));
            setSelectedAccountIds(allIds);
        }
    }, [allAccounts, selectedAccountIds.size, hasManuallyDeselected]);

    // Auto-populate new loan balance when selection changes
    useEffect(() => {
        if (selectedTotals.balance > 0) {
            setNewLoanData(prev => ({
                ...prev,
                balance: selectedTotals.balance.toString()
            }));
        }
    }, [selectedTotals.balance]);

    return (
        <div className="refinance-calculator">
            {/* Header */}
            <div className="refinance-calculator__header">
                <h1 className="refinance-calculator__title">
                    Debt Refinance Calculator
                </h1>
                <p className="refinance-calculator__subtitle">
                    Compare your current debt payments with a potential refinance loan
                </p>
            </div>

            {/* Action Buttons */}
            <div className="refinance-calculator__action-buttons">
                <button
                    className="button button-blue"
                    onClick={getAccounts}
                    disabled={isLoadingAccounts}
                >
                    {isLoadingAccounts ? 'Loading...' : 'Fetch Accounts'}
                </button>
                
                {debtAccounts.length > 0 && (
                    <button
                        className="button button-outline-blue"
                        onClick={handleRefetchAccounts}
                        disabled={isLoadingAccounts}
                    >
                        Refresh Accounts
                    </button>
                )}
                
                <button
                    className="button button-outline"
                    onClick={() => setShowManualForm(true)}
                >
                    Add Manual Account
                </button>
            </div>

            {/* Loading State */}
            {isLoadingAccounts && (
                <div className="refinance-calculator__loading-state">
                    <p className="refinance-calculator__loading-text">
                        ⏳ Loading your accounts...
                    </p>
                </div>
            )}

            {/* No Accounts Message */}
            {!isLoadingAccounts && allAccounts.length === 0 && (
                <div className="refinance-calculator__no-accounts">
                    <h3 className="refinance-calculator__no-accounts-title">
                        Ready to Calculate Your Refinance Options?
                    </h3>
                    <p className="refinance-calculator__no-accounts-text">
                        Click "Fetch Accounts" above to load your debt accounts, or add accounts manually to get started.
                    </p>
                </div>
            )}

            {/* Account List */}
            {allAccounts.length > 0 && (
                <>
                    <div className="refinance-calculator__accounts-section">
                        <div className="refinance-calculator__accounts-header">
                            <div className="refinance-calculator__accounts-header-info">
                                <h2 className="refinance-calculator__accounts-title">
                                    Select Accounts to Refinance
                                </h2>
                                <p className="refinance-calculator__accounts-subtitle">
                                    Check the boxes next to accounts you want to include in your refinance calculation.
                                </p>
                            </div>
                            <div className="refinance-calculator__accounts-controls">
                                <div className="refinance-calculator__sort-control">
                                    <label htmlFor="sort-select" className="refinance-calculator__sort-label">
                                        Sort by:
                                    </label>
                                    <select
                                        id="sort-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="refinance-calculator__sort-select"
                                    >
                                        <option value="balance">Balance (High to Low)</option>
                                        <option value="apr">APR (High to Low)</option>
                                        <option value="monthlyPayment">Monthly Payment (High to Low)</option>
                                    </select>
                                </div>
                                <button
                                    className="button button-outline refinance-calculator__toggle-all-button"
                                    onClick={handleToggleAllAccounts}
                                >
                                    {sortedAccounts.every(account => selectedAccountIds.has(account.id))
                                        ? 'Deselect All' 
                                        : 'Select All'
                                    }
                                </button>
                            </div>
                        </div>
                        
                        <div className="refinance-calculator__accounts-grid">
                            {sortedAccounts.map((account, index) => (
                                <RefinanceAccountItem
                                    key={account.id}
                                    account={account}
                                    isSelected={selectedAccountIds.has(account.id)}
                                    onSelect={handleAccountSelect}
                                    onDelete={account.isManual ? handleManualAccountDelete : null}
                                    isEven={index % 2 === 0}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Selected Totals */}
                    {selectedAccounts.length > 0 && (
                        <div className="refinance-calculator__selected-summary">
                            <h3 className="refinance-calculator__selected-summary-title">
                                Selected Accounts Summary
                            </h3>
                            <div className="refinance-calculator__selected-summary-grid">
                                <div className="refinance-calculator__summary-item">
                                    <strong>Accounts Selected:</strong> {selectedAccounts.length}
                                </div>
                                <div className="refinance-calculator__summary-item">
                                    <strong>Total Balance:</strong> {formatCurrency(selectedTotals.balance)}
                                </div>
                                <div className="refinance-calculator__summary-item">
                                    <strong>Total Monthly Payment:</strong> {formatCurrency(selectedTotals.monthlyPayment)}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* New Loan Form */}
                    {selectedAccounts.length > 0 && (
                        <NewLoanForm
                            newLoanData={newLoanData}
                            setNewLoanData={setNewLoanData}
                            selectedTotals={selectedTotals}
                        />
                    )}

                    {/* Comparison Results */}
                    {comparisonData && (
                        <RefinanceComparison
                            comparisonData={comparisonData}
                            selectedAccounts={selectedAccounts}
                            newLoanData={newLoanData}
                        />
                    )}
                </>
            )}

            {/* Manual Account Form Modal */}
            {showManualForm && (
                <ManualAccountForm
                    onSubmit={handleManualAccountCreate}
                    onCancel={() => setShowManualForm(false)}
                />
            )}
        </div>
    );
};

export default RefinanceCalculator;